import type { ExpressionSpecification, PaddingOptions } from 'mapbox-gl';
import { type ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Source } from 'react-map-gl/mapbox';
import type MapLocation from '@alltrails/amplitude/enums/MapLocation';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import useMap from '../../hooks/useMap';
import { useAppDispatch, useAppSelector } from '../../redux';
import { setTrailheadsInView } from '../../redux/trailhead';
import type { TrailheadInView } from '../../types/Trailhead';
import { expandedTrailheadIds, trailheadsFromFeatures, trailheadsMinZoom, visibleMapQueryBox } from '../../utils/countTrailheadsInView';
import { trailheadsOverlayCollapsedActiveId, trailheadsOverlayCollapsedId, trailheadsOverlayLayerId } from '../../utils/layers';
import pinZoomPopularityMap from '../../utils/pinZoomPopularityMap';
import { buildTrailheadResultExpressions, parseTrailheadTrailIds, trailResultsForIds } from '../../utils/trailheadTrailResults';
import TrailheadVectorPinLayer from '../TrailheadVectorPinLayer/TrailheadVectorPinLayer';
import ActiveTrailPopup from '../TrailResults/ActiveTrailPopup';

type TrailheadVectorOverlayProps = {
  mapLocation?: MapLocation | string; // Accept string for legacy PageStrings support
  mapPadding?: PaddingOptions;
} & Pick<ComponentProps<typeof ActiveTrailPopup>, 'detailedCardLocation'>;

const apiKey = process.env.NEXT_PUBLIC_API_KEY!;
const sourceId = 'trailheads-overlay';
const sourceLayer = 'trailheads';
const pinLayerIds = [trailheadsOverlayLayerId, trailheadsOverlayCollapsedId, trailheadsOverlayCollapsedActiveId];
const inactiveExpression: ExpressionSpecification = ['literal', false];
const collapseEveryPinExpression: ExpressionSpecification = ['literal', true];
const neverHoveredExpression: ExpressionSpecification = ['==', ['id'], -1];
const hasTrailsFilter: ExpressionSpecification = ['>', ['coalesce', ['get', 'trail_count'], 0], 0];

const sameTrailheadIds = (current: TrailheadInView['id'][], next: TrailheadInView['id'][]) =>
  current.length === next.length && current.every((id, index) => id === next[index]);

const sameTrailIds = (current: Record<string, number[]>, next: Record<string, number[]>) => {
  const currentIds = Object.keys(current);
  return (
    currentIds.length === Object.keys(next).length &&
    currentIds.every(trailheadId => {
      const nextTrailIds = next[trailheadId];
      return (
        nextTrailIds !== undefined &&
        nextTrailIds.length === current[trailheadId].length &&
        nextTrailIds.every((trailId, index) => trailId === current[trailheadId][index])
      );
    })
  );
};

export default function TrailheadVectorOverlay({ detailedCardLocation, mapLocation, mapPadding }: TrailheadVectorOverlayProps) {
  const publicBaseUrl = usePublicBaseUrl();
  const map = useMap();
  const dispatch = useAppDispatch();
  const isMobile = useIsMobileSizedScreen();
  const { mapZoomBucket, trailResults } = useAppSelector(state => ({
    mapZoomBucket: state.map.mapZoomBucket,
    trailResults: state.map.trailResults
  }));
  const [expandedIds, setExpandedIds] = useState<TrailheadInView['id'][]>([]);
  const [trailIdsByTrailheadId, setTrailIdsByTrailheadId] = useState<Record<string, number[]>>({});
  const tiles = [`${publicBaseUrl}/wmts/overlay/trailheads/{z}/{x}/{y}?key=${apiKey}`];
  const { maxDesktopRegularPins, maxMobileRegularPins } = pinZoomPopularityMap(mapZoomBucket);
  const maxExpandedPins = isMobile ? maxMobileRegularPins : maxDesktopRegularPins;

  const analyticsInfo = useMemo(
    () => (detailedCardLocation && mapLocation ? { detailedCardLocation, page: mapLocation } : undefined),
    [detailedCardLocation, mapLocation]
  );
  const { isFavorite: isSavedUtil, isComplete: isCompletedUtil } = useReduxListItems(undefined, undefined, analyticsInfo);
  const isTrailSaved = useCallback((trailId: number) => isSavedUtil('trail', trailId), [isSavedUtil]);
  const isTrailCompleted = useCallback((trailId: number) => isCompletedUtil('trail', trailId), [isCompletedUtil]);

  const { isClosedExpression, isCompletedExpression, isSavedExpression, matchedTrailheadsFilter, popularityExpression, trailCountExpression } =
    useMemo(
      () => buildTrailheadResultExpressions(trailResults, trailIdsByTrailheadId, { isTrailCompleted, isTrailSaved }),
      [isTrailCompleted, isTrailSaved, trailIdsByTrailheadId, trailResults]
    );
  const pinFilter = useMemo<ExpressionSpecification>(() => ['all', hasTrailsFilter, matchedTrailheadsFilter], [matchedTrailheadsFilter]);

  // Mirrors what trailCountExpression shows on the label, so ranking can't send an expanded slot to a
  // trailhead with more total trails but fewer that actually matched the current search. Read through a
  // ref rather than a dependency of the idle-driven effect below, so a new search doesn't tear down and
  // re-subscribe that map listener.
  const matchedTrailCountById = useMemo(() => {
    const counts: Record<string, number> = {};
    Object.entries(trailIdsByTrailheadId).forEach(([trailheadId, trailIds]) => {
      const matchedTrailCount = trailResultsForIds(trailResults, trailIds).length;
      if (matchedTrailCount) {
        counts[trailheadId] = matchedTrailCount;
      }
    });
    return counts;
  }, [trailIdsByTrailheadId, trailResults]);
  const matchedTrailCountByIdRef = useRef(matchedTrailCountById);

  useEffect(() => {
    matchedTrailCountByIdRef.current = matchedTrailCountById;
  }, [matchedTrailCountById]);

  // The ranking can only read pins the map already drew, so a trailhead that just came into view has
  // to render once before it can win an expanded pin. It renders as a dot until then, so zooming in
  // can't flash a screen of full pins that the next idle pass immediately takes back. Zoomed all the
  // way in there is no cap, so nothing collapses.
  const isCollapsedExpression = useMemo<ExpressionSpecification>(() => {
    if (maxExpandedPins === undefined) {
      return inactiveExpression;
    }
    return expandedIds.length ? ['!', ['in', ['id'], ['literal', expandedIds]]] : collapseEveryPinExpression;
  }, [expandedIds, maxExpandedPins]);

  useEffect(() => {
    if (!map) {
      return undefined;
    }

    const updateTrailheadsInView = () => {
      // Collapsed pins have to stay in the query so a trailhead that collapsed this pass can still
      // win an expanded pin on the next one.
      const renderedLayerIds = pinLayerIds.filter(layerId => map.getLayer(layerId));
      if (!renderedLayerIds.length || !mapPadding) {
        dispatch(setTrailheadsInView([]));
        return;
      }
      const queryBox = visibleMapQueryBox(map.getCanvas(), mapPadding);
      const features = queryBox
        ? map.queryRenderedFeatures(queryBox, { layers: renderedLayerIds })
        : map.queryRenderedFeatures({ layers: renderedLayerIds });
      const trailheads = trailheadsFromFeatures(features);
      dispatch(setTrailheadsInView(trailheads));
      const rankedTrailheads = trailheads.map(trailhead => {
        const matchedTrailCount = matchedTrailCountByIdRef.current[String(trailhead.id)];
        return matchedTrailCount === undefined ? trailhead : { ...trailhead, trailCount: matchedTrailCount };
      });
      const nextExpandedIds = maxExpandedPins === undefined ? [] : expandedTrailheadIds(rankedTrailheads, maxExpandedPins);
      setExpandedIds(currentIds => (sameTrailheadIds(currentIds, nextExpandedIds) ? currentIds : nextExpandedIds));

      // Read from the source rather than the rendered pins so a trailhead the current filters hide
      // is still known here, and comes back once its trails return to the search results.
      const nextTrailIds: Record<string, number[]> = {};
      map.querySourceFeatures(sourceId, { sourceLayer }).forEach(feature => {
        if (feature.id !== undefined) {
          nextTrailIds[String(feature.id)] = parseTrailheadTrailIds(feature.properties?.trail_ids);
        }
      });
      setTrailIdsByTrailheadId(currentTrailIds => (sameTrailIds(currentTrailIds, nextTrailIds) ? currentTrailIds : nextTrailIds));
    };

    updateTrailheadsInView();
    map.on('idle', updateTrailheadsInView);
    return () => {
      map.off('idle', updateTrailheadsInView);
      dispatch(setTrailheadsInView([]));
    };
  }, [dispatch, map, mapPadding, maxExpandedPins]);

  return (
    <Source id={sourceId} minzoom={trailheadsMinZoom} tiles={tiles} type="vector">
      <TrailheadVectorPinLayer
        collapsedActiveLayerId={trailheadsOverlayCollapsedActiveId}
        collapsedLayerId={trailheadsOverlayCollapsedId}
        filter={pinFilter}
        isClosedExpression={isClosedExpression}
        isCollapsedExpression={isCollapsedExpression}
        isCompletedExpression={isCompletedExpression}
        isHoveredExpression={neverHoveredExpression}
        isSavedExpression={isSavedExpression}
        isSelectedExpression={neverHoveredExpression}
        layerId={trailheadsOverlayLayerId}
        popularityExpression={popularityExpression}
        sourceId={sourceId}
        sourceLayer={sourceLayer}
        trailCountExpression={trailCountExpression}
      />
    </Source>
  );
}
